<template>
  <div id="map" class="map">
    <v-progress-circular
            v-if="geojsons.length === 0"
            indeterminate size="200"
            color="info"
            class="progress-spinner">
      <h4 class="text-lg-center">Please wait for map data to load</h4>
    </v-progress-circular>
    <div v-else class="map-buttons">

      <!--Search-->
      <v-slide-x-transition>
        <div v-show="showLegend">
          <v-btn color="info"
                 slot="activator"
                 class="button-size"
                 @click="searchBar = !searchBar">
            <v-icon>search</v-icon>&nbsp; ATS Search
          </v-btn>
        </div>
      </v-slide-x-transition>

      <!--Base Layers-->
      <v-slide-x-transition>
        <div v-show="showLegend">
          <v-menu offset-y>
            <v-btn color="info"
                   slot="activator"
                   class="button-size">
              <v-icon>map</v-icon>&nbsp; Base Layers
            </v-btn>
            <v-list>
              <v-list-tile
                      v-for="(item, index) in baseLayers"
                      :key="index"
                      @click="activateBaseLayer(item)">
                <v-list-tile-title>{{ item.name }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>

          <v-chip dark color="info">{{ baseLayer }}</v-chip>
        </div>
      </v-slide-x-transition>

      <!--Watersheds-->
      <v-slide-x-transition>
        <div v-show="showLegend">
          <v-menu offset-y>
            <v-btn color="info"
                   slot="activator"
                   class="button-size">
              <v-icon>terrain</v-icon>&nbsp; Watersheds
            </v-btn>
            <v-list>
              <v-list-tile
                      v-for="(item, index) in geojsons.map(ws => { return ws.name })"
                      :key="index"
                      @click="changeWatershed(item)">
                <v-list-tile-title>{{ item }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
          <v-chip dark color="info">{{ currentBasin }}</v-chip>
        </div>
      </v-slide-x-transition>

      <!--Map Indicator Change-->
      <v-slide-x-transition>
        <div v-show="showLegend">
          <v-menu offset-y>
            <v-btn color="info"
                   slot="activator"
                   class="button-size">
              <v-icon>place</v-icon>&nbsp; Indicators
            </v-btn>
            <v-list>
              <v-list-tile
                      v-for="(item, index) in allIndicators"
                      :key="index"
                      @click="changeIndicatorPaint(item)">
                <v-list-tile-title>{{ item }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
          <v-chip dark color="info"
                  @mouseenter="showDescription = true"
                  @mouseleave="showDescription = false"
                  @click="openDescription = true">
            {{ showDescription ? `${indicator} ?` : indicator }}
          </v-chip>
        </div>
      </v-slide-x-transition>

      <!--Map Scenario Change-->
      <v-slide-x-transition>
        <div v-show="showLegend">
          <v-menu offset-y>
            <v-btn color="info"
                   slot="activator"
                   class="button-size">
              <v-icon>place</v-icon>&nbsp; Scenarios
            </v-btn>
            <v-list>
              <v-list-tile
                      v-for="(item, index) in allScenarios"
                      :key="index"
                      @click="changeScenarioPaint(item)">
                <v-list-tile-title>{{ item }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
          <v-chip dark color="info"> {{ scenario }}</v-chip>
        </div>
      </v-slide-x-transition>

      <!--Map Legend-->
      <v-slide-x-transition>
        <div class="gradient-legend">
          <v-card>
            <h4 class="text-lg-center">Indicator Index Value</h4>
            <v-sparkline
                    :value="[1, 1]"
                    :gradient="palette"
                    :smooth="10"
                    :height="20"
                    :padding="8"
                    :line-width="30"
                    :gradient-direction="'left'"
                    auto-draw
            ></v-sparkline>
            <v-layout class="legend-padding">
              <v-flex sm6 class="text-lg-left"><h4>Low (0)</h4></v-flex>
              <v-flex sm6 class="text-lg-right"><h4>High (1)</h4></v-flex>
            </v-layout>
          </v-card>
        </div>
      </v-slide-x-transition>

    </div>
    <v-navigation-drawer
            v-model="openDescription"
            right
            fixed
            temporary
    >
      <v-card dark>
        <v-card-title>
          <v-icon
                  x-large
                  left
          >
            help_outline
          </v-icon>
          <span class="title font-weight-light">{{ indicator }}</span>
        </v-card-title>
      </v-card>
      <v-card-text>{{ indicatorDescription }}</v-card-text>
    </v-navigation-drawer>
    <v-navigation-drawer
            v-model="onboard"
            right
            fixed
            temporary
    >
      <v-card dark>
        <v-card-title>
          <v-icon
                  x-large
                  left
          >
            grade
          </v-icon>
          <span class="title font-weight-light">Welcome!</span>
        </v-card-title>
      </v-card>
      <v-card-text>The Hydrology Data Explorer allows you to view results of hydrological modelling.
        Results are presented for hydrological indicators, which were simulated under various scenarios.<br><br>
        There are two ways to navigate data on this page:<br>
        <br>1. Select an indicator and a scenario to view the results on the map<br>
        <br>2. Click on a sub-basin and click view to a graph. Click on each indicator to add it to the graph in the graph page.</v-card-text>
    </v-navigation-drawer>

    <v-navigation-drawer
            v-model="searchBar"
            right
            fixed
            temporary
    >
      <v-card dark>
        <v-card-title>
          <v-icon
                  x-large
                  left
          >
            my_location
          </v-icon>
          <span class="title font-weight-light">ATS Search</span>
        </v-card-title>
      </v-card>
      <v-container>
        <v-card-text>Search the Alberta Township System (ATS) on the map by entering an LSD below</v-card-text>
        <v-text-field
                clearable
                hint="Enter Meridian, Range, Township, and Section in the form: M-RR-TTT-SS"
                persistent-hint
                mask="#-##-###-##"
                v-model="searchQuery"
                label="5-23-063-26"
                solo
        ></v-text-field>
        <v-slide-y-transition>
          <div>
            <v-btn
                    v-show="searchQuery && searchQuery.length === 8"
                    color="info"
                    @click="mapSearch()"><v-icon>search</v-icon></v-btn>&nbsp;{{ collectingATS ? 'Searching...' : '' }}
            <h3>{{ searchResultsText }}</h3>
            <v-progress-circular
                    v-if="collectingATS"
                    indeterminate size="50"
                    color="info"
                    class="progress-spinner"></v-progress-circular>
          </div>
        </v-slide-y-transition>

      </v-container>
    </v-navigation-drawer>
  </div>
</template>

<script>
  import * as mapboxgl from 'mapbox-gl';
  import * as Rainbow from 'rainbowvis.js'

  export default {
    name: "HydroMap",

    props: {
      geojsons: Array,
      attrs: Array
    },

    beforeMount () {
      // check if the localStorage object is supported by the browser
      if ('localStorage' in window && window['localStorage'] !== null) {
        if ('visitedHydro' in window['localStorage']) {
          this.onboard = false
        } else {
          window['localStorage']['visitedHydro'] = true
        }
      }
    },

    mounted () {
      this.initMap();
      setTimeout(() => { this.showLegend = true }, 1700)
    },

    data() {
      return {
        searchResultsText: '',
        collectingATS: false,
        searchQuery: null,
        searchBar: false,
        onboard: true,
        openDescription: false,
        showDescription: false,
        showLegend: false,
        baseLayer: 'Streets',
        basinID: null,
        currentBasin: null,
        indicator: null,
        scenario: null,

        // Mapbox GL out of the box base layer styles
        baseLayers: [
          {
            name: 'Streets',
            value: 'mapbox://styles/mapbox/streets-v11'
          },
          {
            name: 'Satellite',
            value: 'mapbox://styles/mapbox/satellite-v9'
          },
          {
            name: 'Outdoors',
            value: 'mapbox://styles/mapbox/outdoors-v11'
          },
          {
            name: 'Light',
            value: 'mapbox://styles/mapbox/light-v10'
          },
          {
            name: 'Dark',
            value: 'mapbox://styles/mapbox/dark-v10'
          }
        ],

        mapOptions: {
          container: 'map',
          style: 'mapbox://styles/mapbox/streets-v11',
          center: [-114, 55],
          zoom: 5
        },

        palette: [
          '#fc7171',
          '#fcf070',
          '#17efa7',
          '#70ddfc',
          '#3364ea'
        ],

        palletteValues: [
          '0', '0.25', '0.5', '0.75', '1'
        ],

        indicatorDescriptions: {
          'High Flow Frequency': 'Change in high flow frequency – desired outcome is no change in high flow frequency',
          'Peak Flow Timing': 'Change in peak streamflow timing – desired outcome is no change in peak flow timing',
          'Flashiness Index': 'Change in flashiness index – desired outcome is reduced flashiness',
          'Low Flow Timing': 'Change in low flow timing – desired outcome is later onset of very low flows',
          'Low Flow Index': 'Change in low flow index – desired outcome is reduction in low flow index',
          'Peak Stream Flow': 'Change in peak streamflow – desired outcome is reduced peak flow',
          'Annual Water Yield': 'Change in annual water yield – desired outcome is increased water yield',
          'Low Flow Frequency': 'Change in low flow frequency – desired outcome is no change in the frequency of low flow events'
        }
      }
    },

    methods: {
      initMap () {
        // Ensure data are available
        if (this.geojsons.length === 0 || this.attrs.length === 0) {
          setTimeout(this.initMap, 200);
          return
        }

        // Load the first watershed by default
        let watershed = this.geojsons[0].name;
        this.currentBasin = watershed;

        // Initiate the map instance
        mapboxgl.accessToken = this.$Config.mapbox_gl_token;
        this.map = new mapboxgl.Map(this.mapOptions);

        // Initiate layers on load
        this.map.on('load', () => {

          // Add custom layers
          this.loadSources(watershed);
          this.addLayers();

          // Zoom to the data extent
          this.flyTo(watershed);

          // Watershed click events
          let self = this;
          this.map.on('click', 'basins', function (e) {
            let id = e.features[0].properties.basin_id;
            self.$emit('basin-click', id, self.currentBasin);
            new mapboxgl.Popup()
              .setLngLat(e.lngLat)
              .setHTML(self.basinHTML(id, e.features[0].properties.area))
              .addTo(self.map);
          });

          // Change the cursor to a pointer when the mouse is over the basins layer.
          this.map.on('mouseenter', 'basins', function () {
            self.map.getCanvas().style.cursor = 'pointer';
          });

          // Change it back to a pointer when it leaves.
          this.map.on('mouseleave', 'basins', function () {
            self.map.getCanvas().style.cursor = '';
          });

          this.map.on('style.load', function () {
            self.loadSources(self.currentBasin);
            self.addLayers();
          });

          this.$emit('show-hint', 'Click on a watershed')
        });
      },

      loadSources (watershed) {
        this.map.addSource('dem', {
          "type": "raster-dem",
          "url": "mapbox://mapbox.terrain-rgb"
        });
        this.map.addSource('basins', {
          'type': 'geojson',
          'data': this.geojsons.filter(ws => { return ws.name === watershed} )[0].data
        })
      },

      getMagnitudeExpression () {
        let expression = ['match', ['get', 'basin_id']];
        // Add color palette to the expression
        let palette = new Rainbow();
        palette.setSpectrum(...this.palette);
        // Filter for the indicator and scenario and add colors
        let basinData = this.attrs.filter(ws => {
          return ws.name === this.currentBasin
        })[0].data;

        Object.keys(basinData).forEach(bid => {
          let val = basinData[bid][this.indicator][this.scenario];
          if (val === 0 || isNaN(val)) {
            expression.push(parseInt(bid), '#ffffff')
          } else {
            expression.push(parseInt(bid), '#' + palette.colourAt(val * 100))
          }
        });

        // For no data
        expression.push("rgba(0,0,0,0)");

        return expression
      },

      changeIndicatorPaint (indicator) {
        this.indicator = indicator;
        let expression = this.getMagnitudeExpression();
        this.map.setPaintProperty('basins', 'fill-color', expression);
      },

      changeScenarioPaint (scenario) {
        this.scenario = scenario;
        let expression = this.getMagnitudeExpression();
        this.map.setPaintProperty('basins', 'fill-color', expression);
      },

      addLayers () {
        let firstSymbolId;
        let layers = this.map.getStyle().layers;
        for (let i = 0; i < layers.length; i++) {
          if (layers[i].type === 'symbol') {
            firstSymbolId = layers[i].id;
            break;
          }
        }

        let expression = this.getMagnitudeExpression();

        this.map.addLayer({
          'id': 'basins',
          'type': 'fill',
          'source': 'basins',
          'layout': {},
          'paint': {
            'fill-color': expression,
            'fill-opacity': 0.5
          }
        }, firstSymbolId);

        this.map.addLayer({
          'id': 'basins-stroke',
          'type': 'line',
          'source': 'basins',
          'layout': {},
          'paint': {
            'line-color': '#000000',
            'line-opacity': 1
          }
        }, firstSymbolId);

        // Add a hillshade
        this.map.addLayer({
          "id": "dem-hillshade",
          "source": 'dem',
          "type": "hillshade"
        }, firstSymbolId);
      },

      basinHTML (id, area) {
        /*
        Returns html for a basin popup
         */
        return `<b>Basin ${id}</b><br>Area: ${Math.round(area * 100) / 100} km<sup>2</sup><br><a href="/#/hydro/dummy">View</a>`
      },

      flyTo (watershed) {
        var coordinates = [];
        let geojson = this.geojsons.filter(ws => { return ws.name === watershed })[0].data;
        Object.keys(geojson.features).forEach(key => {
          geojson.features[key].geometry.coordinates.forEach(coord => {
            coordinates.push(...coord)
          })
        });
        var bounds = coordinates.reduce(function(bounds, coord) {
          return bounds.extend(coord);
        }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

        this.map.fitBounds(bounds, {
          padding: 20
        });
      },

      activateBaseLayer (layer) {
        if (layer.name !== this.baseLayer) {
          this.baseLayer = layer.name;
          this.map.setStyle(layer.value)
        }
      },

      changeWatershed (watershed) {
        let geojson = this.geojsons.filter(ws => { return ws.name === watershed })[0];
        this.currentBasin = watershed;
        this.map.getSource('basins').setData(geojson.data);
        let expression = this.getMagnitudeExpression();
        this.map.setPaintProperty('basins', 'fill-color', expression);
        this.flyTo(watershed)
      },

      mapSearch () {
        this.searchResultsText = '';
        if (!this.atsData) {
          this.collectingATS = true;
          this.$API.get('mapApi', '/ats_lsd.json', {}).then(response => {
            this.atsData = response;
            this.moveToSearchCoords();
            this.collectingATS = false;
            this.searchBar = false
          })
        } else {
          this.moveToSearchCoords()
        }
      },

      moveToSearchCoords () {
        let ats = this.searchQuery;
        let twp = ats.slice(3, 6);
        if (twp[0] === '0') {
          twp = twp.slice(1)
        }
        try {
          let coords = this.atsData[ats[0]][ats.slice(1, 3)][twp][ats.slice(6)];
          this.searchBar = false;
          // Add layer
          new mapboxgl.Marker()
            .setLngLat([coords.lng, coords.lat])
            .addTo(this.map);
          this.map.flyTo({
            center: [coords.lng, coords.lat],
            zoom: 12
          });
        } catch (err) {
          this.searchResultsText = "Unable to find the provided location"
        }
      }
    },

    computed: {
      indicatorDescription () {
        return this.indicatorDescriptions[this.indicator]
      },

      allIndicators () {
        if (!this.currentBasin) {
          return []
        } else {
          let basinData = this.attrs.filter(ws => {
            return ws.name === this.currentBasin
          });
          let data = Object.keys(basinData[0].data[0]);
          let self = this;
          if (!this.indicator) {
            self.indicator = data[0]
          }
          return Object.keys(basinData[0].data[0])
        }
      },

      allScenarios () {
        if (!this.currentBasin) {
          return []
        } else {
          let basinData = this.attrs.filter(ws => {
            return ws.name === this.currentBasin
          });
          let data = Object.keys(Object.values(basinData[0].data[0])[0]);
          let self = this;
          if (!this.scenario) {
            self.scenario = data[0]
          }
          return data
        }
      }
    },

    watch: {
      '$route' (to) {
        // Hacky way to activate basin viewer from the mapbox-gl popup box
        if (to.path === '/hydro/dummy') {
          this.$emit('view-basin-data');
          this.$router.push('/hydro');
          this.$emit('show-hint', 'Click on one or more indicators');
        }
      }
    }
  }
</script>

<style scoped>
  .map {
    padding: 0;
    position: relative;
    height: 100%;
    width: 100%;
  }
  .map-buttons {
    position: absolute;
    padding: 5px;
    z-index: 10;
  }
  .button-size {
    min-width: 180px;
  }
  .legend-buttons {
    min-width: 36px;
    padding-left: 0;
    padding-right: 0;
  }
  .progress-spinner {
    margin: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    -ms-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    z-index: 10;
  }
  .gradient-legend {
    padding: 10px;
    max-width: 200px;
  }

  .legend-padding {
    padding: 5px;
  }
</style>
